import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Plus, Edit, Trash2, ArrowLeft, X, Car, Check, XCircle, Search, RefreshCw } from 'lucide-react';
import api from '../services/api';

function Subarrendatarios() {
  const [subarrendatarios, setSubarrendatarios] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [formData, setFormData] = useState({
    empresa_propietaria_id: '',
    cif: '',
    razon_social: '',
    direccion: '',
    codigo_postal: '',
    poblacion: '',
    provincia: '',
    telefono: '',
    email: ''
  });
  const [error, setError] = useState('');
  const [filtro, setFiltro] = useState({ empresa_propietaria_id: '', cif: '', activo: '' });
  const navigate = useNavigate();

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const [subRes, empRes] = await Promise.all([
        api.get('/admin/subarrendatarios', {
          params: filtro.empresa_propietaria_id ? { empresa_propietaria_id: filtro.empresa_propietaria_id } : {}
        }),
        api.get('/admin/empresas')
      ]);
      setSubarrendatarios(subRes.data.data || []);
      setEmpresas(empRes.data.data || []);
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const buscar = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtro.empresa_propietaria_id) params.empresa_propietaria_id = filtro.empresa_propietaria_id;
      if (filtro.cif) params.cif = filtro.cif;
      if (filtro.activo !== '') params.activo = filtro.activo === 'true';

      const response = await api.get('/admin/subarrendatarios', { params });
      setSubarrendatarios(response.data.data || []);
    } catch (error) {
      console.error('Error buscando:', error);
    } finally {
      setLoading(false);
    }
  };

  const abrirModal = (sub = null) => {
    if (sub) {
      setEditando(sub);
      setFormData({
        empresa_propietaria_id: sub.empresa_propietaria_id || '',
        cif: sub.cif || '',
        razon_social: sub.razon_social || '',
        direccion: sub.direccion || '',
        codigo_postal: sub.codigo_postal || '',
        poblacion: sub.poblacion || '',
        provincia: sub.provincia || '',
        telefono: sub.telefono || '',
        email: sub.email || ''
      });
    } else {
      setEditando(null);
      setFormData({
        empresa_propietaria_id: filtro.empresa_propietaria_id || '',
        cif: '',
        razon_social: '',
        direccion: '',
        codigo_postal: '',
        poblacion: '',
        provincia: '',
        telefono: '',
        email: ''
      });
    }
    setError('');
    setShowModal(true);
  };

  const guardar = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editando) {
        await api.put(`/admin/subarrendatarios/${editando.id}`, formData);
      } else {
        await api.post('/admin/subarrendatarios', formData);
      }
      setShowModal(false);
      buscar();
    } catch (error) {
      setError(error.response?.data?.error || 'Error guardando subarrendatario');
    }
  };

  const toggleActivo = async (sub) => {
    try {
      const endpoint = sub.activo ? 'desactivar' : 'activar';
      await api.patch(`/admin/subarrendatarios/${sub.id}/${endpoint}`, {});
      buscar();
    } catch (error) {
      alert(error.response?.data?.error || 'Error actualizando estado');
    }
  };

  const eliminar = async (sub) => {
    if (!window.confirm(`¿Eliminar subarrendatario "${sub.razon_social}"?\n\nEsto solo es posible si no tiene asignaciones asociadas.`)) return;

    try {
      await api.delete(`/admin/subarrendatarios/${sub.id}`);
      buscar();
    } catch (error) {
      alert(error.response?.data?.error || 'Error eliminando subarrendatario');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white/80 backdrop-blur-lg shadow-soft border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin/dashboard')} className="text-slate-600 hover:text-slate-900">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-display">Subarrendatarios</h1>
              <p className="text-sm text-slate-600">Empresas intermediarias que gestionan alquileres de vehiculos</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/admin/asignaciones-subarrendatarios')}
              className="flex items-center gap-2 px-4 py-2 bg-violet-100 text-violet-700 rounded-xl hover:bg-violet-200 transition-colors"
            >
              <Car className="w-5 h-5" />
              Ver Asignaciones
            </button>
            <button
              onClick={() => abrirModal()}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl hover:from-primary-600 hover:to-primary-700 hover:shadow-glow transition-all"
            >
              <Plus className="w-5 h-5" />
              Nuevo Subarrendatario
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Filtros */}
        <div className="bg-white rounded-2xl shadow-soft p-4 mb-6 border border-slate-200">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Empresa Propietaria</label>
              <select
                value={filtro.empresa_propietaria_id}
                onChange={(e) => setFiltro({...filtro, empresa_propietaria_id: e.target.value})}
                className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Todas las empresas</option>
                {empresas.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.nombre}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">CIF</label>
              <input
                type="text"
                value={filtro.cif}
                onChange={(e) => setFiltro({...filtro, cif: e.target.value.toUpperCase()})}
                placeholder="Buscar por CIF..."
                className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Estado</label>
              <select
                value={filtro.activo}
                onChange={(e) => setFiltro({...filtro, activo: e.target.value})}
                className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Todos</option>
                <option value="true">Activos</option>
                <option value="false">Inactivos</option>
              </select>
            </div>
            <div className="flex items-end gap-2">
              <button
                onClick={buscar}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors"
              >
                <Search className="w-4 h-4" />
                Buscar
              </button>
              <button
                onClick={() => { setFiltro({ empresa_propietaria_id: '', cif: '', activo: '' }); cargarDatos(); }}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Tabla */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-slate-600">Cargando...</p>
          </div>
        ) : subarrendatarios.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-soft p-12 text-center border border-slate-200">
            <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No hay subarrendatarios</h3>
            <p className="text-slate-600 mb-4">Crea el primer subarrendatario para comenzar a asignar vehiculos.</p>
            <button
              onClick={() => abrirModal()}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700"
            >
              <Plus className="w-5 h-5" />
              Nuevo Subarrendatario
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-soft overflow-hidden border border-slate-200">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Subarrendatario</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">CIF</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Empresa Propietaria</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">Asignaciones</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">Estado</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {subarrendatarios.map((sub) => (
                  <tr key={sub.id} className={!sub.activo ? 'bg-slate-50' : 'hover:bg-slate-50 transition-colors'}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-violet-600" />
                        </div>
                        <div>
                          <div className="font-medium text-slate-900">{sub.razon_social}</div>
                          {sub.poblacion && <div className="text-sm text-slate-500">{sub.poblacion}, {sub.provincia}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm font-mono text-slate-900">{sub.cif}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{sub.empresa_propietaria_nombre}</td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-violet-100 text-violet-700 text-sm">
                        <Car className="w-4 h-4" />
                        {sub.asignaciones_activas || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => toggleActivo(sub)}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                          sub.activo
                            ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {sub.activo ? 'Activo' : 'Inactivo'}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => abrirModal(sub)}
                          className="p-2 text-slate-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => eliminar(sub)}
                          className="p-2 text-slate-600 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-slate-900">{editando ? 'Editar Subarrendatario' : 'Nuevo Subarrendatario'}</h2>
              <button onClick={() => setShowModal(false)} className="text-slate-500 hover:text-slate-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={guardar} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Empresa Propietaria *</label>
                <select
                  value={formData.empresa_propietaria_id}
                  onChange={(e) => setFormData({...formData, empresa_propietaria_id: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                  required
                  disabled={!!editando}
                >
                  <option value="">Seleccionar empresa...</option>
                  {empresas.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.nombre} ({emp.cif})</option>
                  ))}
                </select>
                {editando && <p className="text-xs text-slate-500 mt-1">La empresa propietaria no se puede cambiar</p>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">CIF *</label>
                  <input
                    type="text"
                    value={formData.cif}
                    onChange={(e) => setFormData({...formData, cif: e.target.value.toUpperCase()})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                    required
                    placeholder="B12345678"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Razon Social *</label>
                  <input
                    type="text"
                    value={formData.razon_social}
                    onChange={(e) => setFormData({...formData, razon_social: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                    required
                    placeholder="Nombre de la empresa"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Direccion *</label>
                <input
                  type="text"
                  value={formData.direccion}
                  onChange={(e) => setFormData({...formData, direccion: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                  required
                  placeholder="Calle, numero, piso..."
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Codigo Postal</label>
                  <input
                    type="text"
                    value={formData.codigo_postal}
                    onChange={(e) => setFormData({...formData, codigo_postal: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                    placeholder="28001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Poblacion</label>
                  <input
                    type="text"
                    value={formData.poblacion}
                    onChange={(e) => setFormData({...formData, poblacion: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                    placeholder="Madrid"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Provincia</label>
                  <input
                    type="text"
                    value={formData.provincia}
                    onChange={(e) => setFormData({...formData, provincia: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                    placeholder="Madrid"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Telefono</label>
                  <input
                    type="tel"
                    value={formData.telefono}
                    onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                    placeholder="912345678"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                    placeholder="contacto@empresa.com"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all"
                >
                  {editando ? 'Guardar Cambios' : 'Crear Subarrendatario'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Subarrendatarios;
